import { forwardRef, type KeyboardEvent as ReactKeyboardEvent, type MouseEvent as ReactMouseEvent } from 'react';
import classNames from 'classnames/bind';
import { Theme } from '../types/enums';
import { type IconButtonProps, type IconButtonTheme } from '../IconButton/types/propsType';
import { FAIcon } from '../Icons/Icons';
import { useTranslation } from '../Translation/TranslationProvider';
import { KeyConstants } from '../Accordion/types/enums';
import { DynamicObjectInterface } from '../Table';
import styles from './IconButton.module.scss';
import { IconButtonSize } from './types/enums';

const cx = classNames.bind(styles);

const IconButton = forwardRef<HTMLSpanElement, IconButtonProps>((props: IconButtonProps, ref) => {
  const {
    disabled,
    iconWithBorder = false,
    theme = Theme.BLACK,
    containerClassName = '',
    className,
    onClick,
    ariaLabel,
    size = IconButtonSize.DEFAULT,
    ...rest
  } = props;
  const t = useTranslation();
  const keys = [KeyConstants.ENTER, KeyConstants.SPACE];

  const ariaLabelValue = ariaLabel || t('nextgen.IconButton.ariaLabel');

  const onClickHandler = (e: ReactKeyboardEvent | ReactMouseEvent) => onClick?.(e);

  const shouldHandleKeyDown = (e: DynamicObjectInterface) => {
    if (keys.includes(e.code as KeyConstants)) {
      onClick?.(e);
    }
  };

  const getCls = cx(
    'iconbutton-container',
    {
      'disabled-icon': disabled,
      'border-icon': !disabled && iconWithBorder,
    },
    [theme, size],
    containerClassName,
  );
  const getIconButtonCls = cx('iconbutton', 'icon', className);
  return (
    <span
      tabIndex={0}
      role="button"
      aria-label={ariaLabelValue}
      onClick={onClickHandler}
      onKeyDown={shouldHandleKeyDown}
      data-testid="icon-button-test-id"
      className={getCls}
    >
      <FAIcon className={getIconButtonCls} {...rest} ref={ref} />
    </span>
  );
});
IconButton.displayName = 'IconButton';
export default IconButton;
export type { IconButtonProps, IconButtonTheme };
